// SHGM code → human label. Zone codes mirror spec/fixtures/shgm-legend.json (from the authoritative
// ApprovedReservedAreas catalog); NOTAM area classes are the common aviation codes we can name with
// confidence — unknown codes are shown verbatim (never invented). Used for tooltips/popups + filters.

export const ZONE_AREA_TYPE: Record<number, string> = { 908: 'Yeşil', 909: 'Kırmızı' };

export const ZONE_AUTHORITY: Record<number, string> = {
  0: 'Tanımsız',
  918: 'Genelkurmay',
  919: 'Mülki İdare - Valilik',
  920: 'SHGM',
  923: 'Mülki İdare - Kaymakamlık',
  928: 'DHMİ',
};

// The SHGM NOTAM `AreaType` field carries ICAO NOTAM Q-codes. There are ~24 distinct values in the live
// data, most cryptic (WULW, WELW, RRCS…). We decode them: the "W?LW" family is Navigation Warnings whose
// SECOND letter is the ICAO warning subject (WU=unmanned/drone, WE=military exercise, WM=gun/rocket firing,
// WP=parachute, …); P/R/D/CTR/TMA are the classic airspace classes; a handful (OBCE/ACCH/FAXX) we can't
// name confidently and leave as the raw code rather than invent a meaning.

// ICAO Q-code warning subjects (2nd letter of a W-family code).
const W_SUBJECT: Record<string, { label: string; desc: string }> = {
  A: { label: 'Hava gösterisi', desc: 'Hava gösterisi (air display) yapılıyor.' },
  B: { label: 'Akrobasi uçuşu', desc: 'Akrobasi uçuşu (aerobatics).' },
  C: { label: 'Bağlı balon / uçurtma', desc: 'Bağlı balon ya da uçurtma.' },
  D: { label: 'Patlayıcı imhası', desc: 'Patlayıcı madde imhası.' },
  E: { label: 'Askeri tatbikat', desc: 'Askeri tatbikat / eğitim uçuşu.' },
  F: { label: 'Havada yakıt ikmali', desc: 'Havada yakıt ikmali.' },
  G: { label: 'Planör uçuşu', desc: 'Planör (glider) faaliyeti.' },
  H: { label: 'Patlatma', desc: 'Patlatma (blasting) çalışması.' },
  J: { label: 'Flama / hedef çekme', desc: 'Flama ya da hedef çekme uçuşu.' },
  L: { label: 'Serbest balon', desc: 'Serbest balon salınımı.' },
  M: { label: 'Füze / top / roket atışı', desc: 'Füze, top ya da roket atış faaliyeti — yüksek risk.' },
  P: { label: 'Paraşüt / yamaç paraşütü', desc: 'Paraşüt ya da yamaç paraşütü atlayışı.' },
  R: { label: 'Radyoaktif / toksik madde', desc: 'Radyoaktif ya da toksik madde.' },
  S: { label: 'Gaz yakma / üfleme', desc: 'Gaz yakma ya da üfleme.' },
  T: { label: 'Toplu uçak hareketi', desc: 'Toplu / yoğun uçak hareketi.' },
  U: { label: 'İHA / drone faaliyeti', desc: 'İnsansız hava aracı (İHA/drone) faaliyeti.' },
  V: { label: 'Formasyon uçuşu', desc: 'Formasyon uçuşu.' },
  W: { label: 'Volkanik faaliyet', desc: 'Önemli volkanik faaliyet.' },
  Y: { label: 'Havadan keşif / ölçüm', desc: 'Havadan keşif ya da ölçüm uçuşu.' },
  Z: { label: 'Model uçak', desc: 'Model uçak faaliyeti.' },
};

// Classic (non-W) airspace classes + a few SHGM-specific codes.
const NOTAM_FIXED: Record<string, { label: string; desc: string }> = {
  P: { label: 'Yasak saha (P)', desc: 'Uçuş sürekli ve kesin yasak (Prohibited). İzinle bile girilemez.' },
  R: { label: 'Kısıtlı saha (R)', desc: 'Kısıtlı saha (Restricted): yalnız belirli koşullarda / izinle uçulabilir.' },
  RAS: { label: 'Ayrılmış hava sahası (RAS)', desc: 'Ayrılmış hava sahası (Reserved): askeri/özel kullanıma ayrılmış.' },
  RRCA: { label: 'Kısıtlı saha (RRCA)', desc: 'Kısıtlı/ayrılmış hava sahası (SHGM RRCA kodu).' },
  RRCS: { label: 'Kısıtlı saha (RRCS)', desc: 'Kısıtlı/ayrılmış hava sahası (SHGM RRCS kodu).' },
  RPCA: { label: 'Kısıtlı saha (RPCA)', desc: 'Kısıtlı/ayrılmış hava sahası (SHGM RPCA kodu).' },
  RTLP: { label: 'Kısıtlı saha (RTLP)', desc: 'Kısıtlı/ayrılmış hava sahası (SHGM RTLP kodu).' },
  D: { label: 'Tehlikeli saha (D)', desc: 'Tehlikeli saha (Danger): atış, patlatma gibi riskli faaliyet olabilir — dikkat.' },
  D_OTHER: { label: 'Tehlikeli saha (diğer)', desc: 'Diğer tehlikeli saha faaliyeti.' },
  CTR: { label: 'Kontrol bölgesi (CTR)', desc: 'Havaalanı kontrol bölgesi (Control Zone): kule izni olmadan uçulmaz.' },
  TMA_P: { label: 'Terminal saha (TMA)', desc: 'Terminal kontrol sahası (TMA): havaalanı çevresi yoğun hava trafiği.' },
  ACCH: { label: 'Saha kontrol değişikliği (ACCH)', desc: 'Saha kontrol değişikliği (SHGM ACCH kodu).' },
  OBCE: { label: 'Engel / mania (OBCE)', desc: 'Yükseltilmiş engel/mania (SHGM OBCE kodu).' },
  FAXX: { label: 'Tesis / diğer (FAXX)', desc: 'Tesis ya da diğer bildirim (SHGM FAXX kodu).' },
};

/** A W-family code looks like `W<subject>LW` (4 chars); return the subject entry if so. */
function wSubject(code: string): { label: string; desc: string } | null {
  return /^W[A-Z]LW$/.test(code) ? (W_SUBJECT[code[1]] ?? null) : null;
}

/** NOTAM/airspace family for grouping — key + human label. */
export type NotamFamily = { key: string; label: string };
export function notamFamily(code: string): NotamFamily {
  if (code === 'P') return { key: 'prohibited', label: 'Yasak sahalar' };
  if (code === 'D' || code === 'D_OTHER') return { key: 'danger', label: 'Tehlikeli sahalar' };
  if (code === 'CTR' || code.startsWith('TMA')) return { key: 'control', label: 'Kontrol / terminal sahaları' };
  if (code === 'R' || code === 'RAS' || /^R[A-Z]/.test(code)) return { key: 'restricted', label: 'Kısıtlı / ayrılmış sahalar' };
  if (/^W[A-Z]LW$/.test(code)) return { key: 'warning', label: 'Uyarı sahaları (faaliyet)' };
  return { key: 'other', label: 'Diğer kodlar' };
}

export const notamTypeLabel = (code: string): string => {
  if (NOTAM_FIXED[code]) return NOTAM_FIXED[code].label;
  const w = wSubject(code);
  return w ? `${w.label} (${code})` : code;
};
export const notamTypeDesc = (code: string): string => {
  if (NOTAM_FIXED[code]) return NOTAM_FIXED[code].desc;
  const w = wSubject(code);
  return w ? `Uyarı sahası — ${w.desc}` : '';
};

/**
 * Why a zone is restricted — inferred from its SHGM catalog NAME (the catalog has no structured
 * "reason" field). Heuristic keyword match on Turkish names; anything unmatched stays "Diğer" so we
 * never invent a reason. Drives the red-zone sub-breakdown filter and the tooltip/info-card "why".
 */
export type ZoneCategory = { key: string; label: string; icon: string };
const CATEGORY_RULES: { key: string; label: string; icon: string; re: RegExp }[] = [
  { key: 'nuclear', label: 'Nükleer', icon: '☢', re: /nükleer|reaktör|akkuyu|nuclear/i },
  { key: 'military', label: 'Askeri', icon: '🛡️', re: /aske|kışla|ordu|garnizon|tatbikat|atış|tank|piyade|jandarma|topçu|\büss?\b|mühimmat|karargâh|karargah/i },
  { key: 'airport', label: 'Havacılık', icon: '✈', re: /havaliman|havaalan|hava üss|meydan|pist|heliport|apron|uçuş okulu/i },
  { key: 'dam', label: 'Baraj / su', icon: '🌊', re: /baraj|gölet|hidroelektrik|\bhes\b|regülatör|regulator/i },
  { key: 'protected', label: 'Koruma / SİT', icon: '🌳', re: /\bsit\b|milli park|tabiat|yaban hayat|koruma|\bpark\b|orman|antik|ören|höyük|arkeolo/i },
  { key: 'prison', label: 'Ceza infaz', icon: '🔒', re: /cezaevi|ceza infaz|tutukevi|\bf tipi\b|açık ceza/i },
  { key: 'energy', label: 'Enerji / tesis', icon: '⚡', re: /rafineri|petrol|doğal ?gaz|santral|termik|enerji|petrokimya|boru hattı/i },
  { key: 'border', label: 'Sınır / hudut', icon: '🛑', re: /sınır|hudut|\bborder\b/i },
];
export const OTHER_CATEGORY: ZoneCategory = { key: 'other', label: 'Diğer', icon: '•' };
// Memoised by name: zoneCategory is called 5–6× per zone across the on-load builders (search index, zone
// FCs, POI FC, facet discovery) — ~50k calls for the full SHGM dataset, each a Turkish-lowercase + regex
// sweep. The result is a pure function of the name, so caching it turns the repeat calls into O(1) lookups.
const categoryCache = new Map<string, ZoneCategory>();
export function zoneCategory(name: string): ZoneCategory {
  const cached = categoryCache.get(name);
  if (cached) return cached;
  // Turkish-locale lowercasing so the dotted İ (SİT, İnfaz) folds to a plain "i" the patterns match.
  const n = name.toLocaleLowerCase('tr');
  let result: ZoneCategory = OTHER_CATEGORY;
  for (const r of CATEGORY_RULES) if (r.re.test(n)) { result = { key: r.key, label: r.label, icon: r.icon }; break; }
  categoryCache.set(name, result);
  return result;
}
export const categoryLabel = (key: string): string =>
  (CATEGORY_RULES.find((r) => r.key === key) ?? OTHER_CATEGORY).label;
export const categoryIcon = (key: string): string =>
  (CATEGORY_RULES.find((r) => r.key === key) ?? OTHER_CATEGORY).icon;

// The full category list (key + label + emoji) — the SINGLE source of truth for symbols. The map, the
// filter chips, the legend, and tooltips all draw from `icon` here, so a category looks the same
// everywhere. (Previously the map used a separate basemap-sprite set that drifted from these emojis —
// e.g. military showed a skull sprite while the filter showed a target.)
export const CATEGORIES: ZoneCategory[] = CATEGORY_RULES.map(({ key, label, icon }) => ({ key, label, icon }));
// Whether a category gets its own map symbol (every real reason does; 'other' stays icon-less — the zone
// fill still carries it).
export const categoryHasMapSymbol = (key: string): boolean => key !== 'other' && CATEGORY_RULES.some((r) => r.key === key);

export const zoneAreaLabel = (code: number | null): string => (code == null ? '' : (ZONE_AREA_TYPE[code] ?? `#${code}`));
export const zoneAuthorityLabel = (code: number | null): string => (code == null ? '' : (ZONE_AUTHORITY[code] ?? `#${code}`));
